import { useEffect, useMemo, useRef, useState } from 'react';
import type { LockVersion } from '@/services/loanPricingService';
import { lockRateValue } from '@/services/lockNetBuyRate';
import {
  findCurrentObRun,
  findLiveMatchingRun,
  getReviewableMaterialRuns,
  isManualCaptureLockVersion,
  isSelectableLockVersion
} from './lockMaterialRuns';
import { jsonStableEqual } from '../utils/loanValueHelpers';

type RowAction = 'active-pill' | 'set-active' | 'none';
type LockMeta = { label: string; value: string };

const EMPTY_LOCK_VERSIONS: LockVersion[] = [];

interface LockHistoryPickerProps {
  lockVersions?: LockVersion[];
  selectedLockVersionId?: string | null;
  onLockVersionSelect?: (id: string | null) => void;
  isViewingSnapshot: boolean;
  /** lock_version_id of the active delivery lock, if any. */
  activeLockVersionId?: string | null;
  isApplyingActive?: boolean;
  pricingDirty?: boolean;
  /** (liveLoan ?? loan).rate_lock_status === 'Locked' — un-overlaid status. */
  loanIsLocked?: boolean;
  liveLockFields?: Record<string, unknown> | null;
  liveMaterialLockHash?: string | null;
  liveMaterialLockHashLoaded?: boolean;
  onSetActiveFromLockVersion?: (version: LockVersion) => void;
  /**
   * Blocks only the header Set Active (the inspected / current target), e.g.
   * "Below par — cover the shortfall first". Row buttons for other locks stay
   * open; the server judges each target.
   */
  headerBlockedReason?: string;
  /** Blocks every Set Active, e.g. while a pricing save is pending. */
  allBlockedReason?: string;
  /** Title shown on disabled buttons while pricing is dirty (mode-specific copy). */
  dirtyTitle?: string;
  /** @deprecated transitional alias for pre-lock_versions callers/tests. */
  snapshots?: LockVersion[];
  /** @deprecated transitional alias for pre-lock_versions callers/tests. */
  selectedSnapshotId?: string | null;
  /** @deprecated transitional alias for pre-lock_versions callers/tests. */
  onSnapshotSelect?: (id: string | null) => void;
  /** @deprecated transitional alias for pre-lock_versions callers/tests. */
  activeSnapshotId?: string | null;
  /** @deprecated transitional alias for pre-lock_versions callers/tests. */
  onSetActiveFromSnapshot?: (version: LockVersion) => void;
}

function formatCapturedAt(iso: string | undefined): string {
  if (!iso) {
    return '—';
  }
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  } catch {
    return '—';
  }
}

function relativeAgo(iso: string | undefined): string {
  if (!iso) {
    return '';
  }
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffDays = Math.floor((now - then) / (1000 * 60 * 60 * 24));
  if (diffDays < 1) {
    const diffHrs = Math.floor((now - then) / (1000 * 60 * 60));
    return diffHrs <= 1 ? 'just now' : `${diffHrs}h ago`;
  }
  if (diffDays < 7) {
    return `${diffDays}d ago`;
  }
  const diffWks = Math.floor(diffDays / 7);
  return `${diffWks}w ago`;
}

function hasValue(value: unknown): boolean {
  return value !== null && value !== undefined && value !== '';
}

function formatNumber(value: unknown, digits: number): string {
  if (!hasValue(value)) {
    return '—';
  }
  const n = Number(String(value).replace(/[$,%\s,]/g, ''));
  return Number.isFinite(n) ? n.toFixed(digits) : String(value);
}

function formatDateOnly(value: unknown): string {
  if (!hasValue(value)) {
    return '—';
  }
  const raw = String(value);
  const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) {
    return `${ymd[2]}/${ymd[3]}/${ymd[1]}`;
  }
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) {
    return raw;
  }
  return d.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric'
  });
}

function lockMeta(fields: Record<string, unknown> | null | undefined): LockMeta[] {
  const source = fields ?? {};
  const product = hasValue(source.rate_lock_loan_program) ? String(source.rate_lock_loan_program) : '—';
  const rate = formatNumber(lockRateValue(source), 3);
  const price = formatNumber(source.rate_lock_total_buy_price, 3);
  const totalAdjustment = formatNumber(source.rate_lock_price_total_adjustment, 3);
  const days = hasValue(source.rate_lock_buy_side_number_of_days)
    ? `${formatNumber(source.rate_lock_buy_side_number_of_days, 0)} days`
    : '—';
  const expires = formatDateOnly(source.rate_lock_buy_side_lock_expires);

  return [
    { label: 'Product', value: product },
    { label: 'Rate', value: rate === '—' ? rate : `${rate}%` },
    { label: 'Total Price Locked', value: price },
    { label: 'Total Adj', value: totalAdjustment },
    { label: 'Days', value: days },
    { label: 'Expires', value: expires }
  ];
}

function metaSignature(fields: Record<string, unknown> | null | undefined): string {
  return lockMeta(fields).map((m) => `${m.label}:${m.value}`).join('|');
}

function payloadValueChanged(
  currentPayload: Record<string, unknown>,
  previousPayload: Record<string, unknown>,
  key: string
): boolean {
  return !jsonStableEqual(currentPayload[key], previousPayload[key]);
}

function changedPayloadKeys(
  currentPayload: Record<string, unknown>,
  previousPayload: Record<string, unknown>
): Set<string> {
  const keys = new Set([...Object.keys(currentPayload), ...Object.keys(previousPayload)]);
  return new Set(Array.from(keys).filter((key) => (
    key !== 'rate_lock_buy_side_adjustments'
    && payloadValueChanged(currentPayload, previousPayload, key)
  )));
}

function materialChangeReason(current: LockVersion, previous: LockVersion | null): string | null {
  if (!previous || metaSignature(current.rateLockFields) !== metaSignature(previous.rateLockFields)) {
    return null;
  }

  const currentPayload = current.materialLockPayload ?? {};
  const previousPayload = previous.materialLockPayload ?? {};
  if (jsonStableEqual(currentPayload, previousPayload)) {
    return null;
  }

  const changedKeys = changedPayloadKeys(currentPayload, previousPayload);
  if (changedKeys.size === 0) {
    return null;
  }
  if (changedKeys.has('rate_lock_price_total_adjustment')) {
    return 'Total adjustment changed';
  }
  if (
    changedKeys.has('rate_lock_ltv')
    || changedKeys.has('rate_lock_cltv')
    || changedKeys.has('rate_lock_hcltv')
  ) {
    return 'LTV details changed';
  }
  if (
    changedKeys.has('rate_lock_property_appraised_value')
    || changedKeys.has('rate_lock_property_estimated_value')
  ) {
    return 'Property value changed';
  }
  if (changedKeys.has('rate_lock_base_loan_amount')) {
    return 'Loan amount changed';
  }
  if (
    changedKeys.has('rate_lock_sell_side_rate')
    || changedKeys.has('rate_lock_total_sell_price')
    || changedKeys.has('rate_lock_sell_side_lock_date')
    || changedKeys.has('rate_lock_sell_side_lock_expires')
  ) {
    return 'Sell-side lock changed';
  }
  return 'Material lock details changed';
}

/**
 * Encompass Lock Versions picker — custom dropdown matching the handoff's
 * `.version-picker` / `.vp-*` class system.
 *
 * This is a pure Encompass/OB lock-data ledger. It is not local Product &
 * Pricing history: selecting rows changes lock-derived fields only, while
 * discount, BPC, lender credit, LOCR, concessions, and other local pricing
 * inputs remain live.
 *
 * Each row carries TWO controls:
 *   - an inspect button (`.vp-item-inspect`) that updates `selectedLockVersionId`
 *   - a Set Active button (`.vp-item-action`) OR an `Active` pill on the right
 *
 * There is no normal synthetic Current row. Live OB state appears as Current
 * OB / Matches Live / Current OB not saved yet metadata. The Active pill
 * belongs to the saved lock-version row pointed at by `activeLockVersionId`.
 *
 * Markup is sibling-buttons-in-a-div, never a button-in-a-button. ARIA
 * `role="listbox"` was removed when the rows gained a second interactive
 * control — single-select listbox semantics no longer fit.
 */
export function LockHistoryPicker({
  lockVersions,
  selectedLockVersionId,
  onLockVersionSelect,
  isViewingSnapshot,
  activeLockVersionId,
  isApplyingActive = false,
  pricingDirty = false,
  loanIsLocked = false,
  liveLockFields = null,
  liveMaterialLockHash = null,
  liveMaterialLockHashLoaded = true,
  onSetActiveFromLockVersion,
  headerBlockedReason,
  allBlockedReason,
  dirtyTitle,
  snapshots: legacySnapshots,
  selectedSnapshotId: legacySelectedSnapshotId,
  onSnapshotSelect: legacyOnSnapshotSelect,
  activeSnapshotId: legacyActiveSnapshotId,
  onSetActiveFromSnapshot: legacyOnSetActiveFromSnapshot
}: LockHistoryPickerProps) {
  const [open, setOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const resolvedLockVersions = useMemo(
    () => lockVersions ?? legacySnapshots ?? EMPTY_LOCK_VERSIONS,
    [lockVersions, legacySnapshots]
  );
  const resolvedSelectedLockVersionId = selectedLockVersionId ?? legacySelectedSnapshotId ?? null;
  const resolvedOnLockVersionSelect = onLockVersionSelect ?? legacyOnSnapshotSelect;
  const resolvedActiveLockVersionId = activeLockVersionId ?? legacyActiveSnapshotId ?? null;
  const resolvedOnSetActiveFromLockVersion = onSetActiveFromLockVersion ?? legacyOnSetActiveFromSnapshot;

  const selectedLockVersion = useMemo(
    () => (resolvedSelectedLockVersionId ? resolvedLockVersions.find((s) => s.id === resolvedSelectedLockVersionId) ?? null : null),
    [resolvedSelectedLockVersionId, resolvedLockVersions]
  );
  const runs = useMemo(
    () => getReviewableMaterialRuns(resolvedLockVersions, resolvedActiveLockVersionId),
    [resolvedLockVersions, resolvedActiveLockVersionId]
  );
  const selectedRun = useMemo(
    () => resolvedSelectedLockVersionId
      ? runs.find((run) => run.snapshots.some((version) => version.id === resolvedSelectedLockVersionId)) ?? null
      : null,
    [runs, resolvedSelectedLockVersionId]
  );
  const activeRun = useMemo(
    () => runs.find((run) => run.hasActive) ?? null,
    [runs]
  );
  const currentObRun = useMemo(
    () => findCurrentObRun(runs, liveMaterialLockHash),
    [runs, liveMaterialLockHash]
  );
  const liveMatchingRun = useMemo(
    () => findLiveMatchingRun(runs, liveMaterialLockHash),
    [runs, liveMaterialLockHash]
  );
  const showUnsavedCurrentOb =
    loanIsLocked &&
    liveMaterialLockHashLoaded &&
    !!liveMaterialLockHash &&
    !liveMatchingRun;
  const unsavedCurrentObIsInspected = showUnsavedCurrentOb && !activeRun && !isViewingSnapshot;

  useEffect(() => {
    if (!open) {
      return;
    }
    const handler = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (resolvedSelectedLockVersionId && selectedRun?.hasActive) {
      resolvedOnLockVersionSelect?.(null);
    }
  }, [resolvedOnLockVersionSelect, selectedRun, resolvedSelectedLockVersionId]);

  const select = (id: string | null) => {
    resolvedOnLockVersionSelect?.(id && id === resolvedActiveLockVersionId ? null : id);
    setOpen(false);
  };

  const rowActionForRun = (run: (typeof runs)[number]): RowAction => {
    if (run.hasActive) {
      return 'active-pill';
    }
    if (!resolvedOnSetActiveFromLockVersion || !isSelectableLockVersion(run.setActiveRepresentative)) {
      return 'none';
    }
    return 'set-active';
  };

  const selectRun = (run: (typeof runs)[number]) => {
    resolvedOnLockVersionSelect?.(run.hasActive ? null : run.displayRepresentative.id);
    setOpen(false);
  };

  const rowActionForInspectedRow = (): RowAction => {
    if (resolvedSelectedLockVersionId === null) {
      if (activeRun) {
        return 'active-pill';
      }
      if (
        currentObRun
        && resolvedOnSetActiveFromLockVersion
        && isSelectableLockVersion(currentObRun.setActiveRepresentative)
      ) {
        return 'set-active';
      }
      return 'none';
    }
    if (resolvedSelectedLockVersionId === resolvedActiveLockVersionId) {
      return 'active-pill';
    }
    if (runs.some((run) => run.hasActive && run.snapshots.some((s) => s.id === resolvedSelectedLockVersionId))) {
      return 'active-pill';
    }
    if (!resolvedOnSetActiveFromLockVersion) {
      return 'none';
    }
    const inspected = selectedRun?.setActiveRepresentative ?? resolvedLockVersions.find((s) => s.id === resolvedSelectedLockVersionId);
    return inspected && isSelectableLockVersion(inspected) ? 'set-active' : 'none';
  };

  const fireSetActiveForInspectedRow = () => {
    if (resolvedSelectedLockVersionId === null) {
      if (activeRun) {
        return;
      }
      if (currentObRun && isSelectableLockVersion(currentObRun.setActiveRepresentative)) {
        resolvedOnSetActiveFromLockVersion?.(currentObRun.setActiveRepresentative);
        return;
      }
      return;
    }
    const inspected = selectedRun?.setActiveRepresentative ?? resolvedLockVersions.find((s) => s.id === resolvedSelectedLockVersionId);
    if (inspected && isSelectableLockVersion(inspected)) {
      resolvedOnSetActiveFromLockVersion?.(inspected);
    }
    // else: defensive no-op — selectedLockVersionId points at a stale version
  };

  const headerAction = rowActionForInspectedRow();
  const dirtyReason = pricingDirty ? (dirtyTitle ?? 'Save pricing changes before setting active') : undefined;
  const setActiveTooltip = allBlockedReason ?? dirtyReason;
  const setActiveDisabled = isApplyingActive || pricingDirty || !!allBlockedReason;
  // Below par blocks only the inspected target; a better lock in the rows stays one click.
  const headerDisabled = setActiveDisabled || !!headerBlockedReason;
  const headerTooltip = setActiveTooltip ?? headerBlockedReason;

  const totalVersions = runs.length + (showUnsavedCurrentOb ? 1 : 0);
  const headerSnapshot = resolvedSelectedLockVersionId
    ? selectedRun?.displayRepresentative ?? selectedLockVersion
    : activeRun?.displayRepresentative ?? currentObRun?.displayRepresentative ?? null;
  const headerTitle = resolvedSelectedLockVersionId
    ? headerSnapshot?.label ?? 'Lock version'
    : headerSnapshot?.label ?? (showUnsavedCurrentOb ? 'Current OB not saved yet' : 'Lock Versions');
  const headerSubtitle = resolvedSelectedLockVersionId
    ? selectedRun?.hasActive
      ? 'Active delivery lock'
      : formatCapturedAt(headerSnapshot?.capturedAt)
    : activeRun
      ? 'Active delivery lock'
      : currentObRun
        ? 'Current OB lock version'
        : showUnsavedCurrentOb
          ? 'Wait for sync, refresh, or contact ops'
          : 'Select a lock version';

  return (
    <>
      <div className="ls-eyebrow">
        Encompass Lock Versions
        <span className="ver-count">
          {totalVersions} version{totalVersions === 1 ? '' : 's'}
        </span>
      </div>
      <div className="version-picker" ref={pickerRef}>
        <div className="version-picker-header">
          <button
            type="button"
            className="vp-current"
            aria-haspopup="menu"
            aria-expanded={open}
            onClick={() => setOpen((prev) => !prev)}
          >
            <div>
              <div className="vp-title">{headerTitle}</div>
              <div className="vp-sub">{headerSubtitle}</div>
            </div>
            <svg
              className="vp-chev"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </button>
          {headerAction === 'active-pill' && (
            <span className="vp-header-active-pill">Active</span>
          )}
          {headerAction === 'set-active' && (
            <button
              type="button"
              className="vp-header-action"
              disabled={headerDisabled}
              title={headerTooltip}
              onClick={(e) => {
                e.stopPropagation();
                fireSetActiveForInspectedRow();
              }}
            >
              Set Active
            </button>
          )}
        </div>
        <div className="vp-menu" hidden={!open}>
          <div className="vp-menu-hdr">
            <div className="vp-menu-t">Encompass Lock Versions</div>
            <div className="vp-menu-s">OB lock data versions</div>
          </div>
          {showUnsavedCurrentOb && (
            <div className="vp-item-row vp-item-row-exception">
              <button
                type="button"
                className={`vp-item-inspect${unsavedCurrentObIsInspected ? ' active' : ''}`}
                onClick={() => select(null)}
              >
                <div>
                  <div className="vp-item-t">
                    Current OB not saved yet <span className="vp-badge vp-badge-live">Not saved</span>
                  </div>
                  <div className="vp-item-s">Current locked OB data has not been saved by sync yet. Refresh after sync or contact ops.</div>
                  <div className="vp-item-meta">
                    {lockMeta(liveLockFields).map((m) => (
                      <span className="vp-meta-chip" key={m.label}>
                        <span className="vp-meta-label">{m.label}</span>
                        <span className="vp-meta-value">{m.value}</span>
                      </span>
                    ))}
                  </div>
                </div>
                {unsavedCurrentObIsInspected && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden>
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                )}
              </button>
            </div>
          )}
          {runs.map((run, index) => {
            const s = run.displayRepresentative;
            const previousVisibleRun = runs[index + 1] ?? null;
            const changeReason = materialChangeReason(s, previousVisibleRun?.displayRepresentative ?? null);
            const ver = typeof s.version === 'number' ? `V${s.version}` : null;
            const isInspected = resolvedSelectedLockVersionId
              ? run.snapshots.some((snap) => snap.id === resolvedSelectedLockVersionId)
              : run.hasActive && !isViewingSnapshot;
            const action = rowActionForRun(run);
            const isCurrentOb = currentObRun?.id === run.id;
            const isLiveMatch = liveMatchingRun?.id === run.id;
            const showMatchesLive = isLiveMatch && !isCurrentOb;
            const isSelectable = isSelectableLockVersion(s);
            const isLegacyRecovery = isManualCaptureLockVersion(s);
            return (
              <div className="vp-item-row" key={run.id}>
                <button
                  type="button"
                  className={`vp-item-inspect${isInspected ? ' active' : ''}`}
                  onClick={() => selectRun(run)}
                >
                  <div>
                    <div className="vp-item-t">
                      {s.label || 'Lock version'} {ver && <span className="vp-ver">{ver}</span>}
                      {isCurrentOb && <span className="vp-badge vp-badge-current">Current OB</span>}
                      {showMatchesLive && <span className="vp-badge vp-badge-live">Matches Live</span>}
                      {isLegacyRecovery && <span className="vp-badge vp-badge-readonly">Legacy recovery</span>}
                      {!isSelectable && <span className="vp-badge vp-badge-readonly">Read only</span>}
                    </div>
                    <div className="vp-item-s mono">{formatCapturedAt(s.capturedAt)}</div>
                    <div className="vp-item-meta">
                      {lockMeta(s.rateLockFields).map((m) => (
                        <span className="vp-meta-chip" key={m.label}>
                          <span className="vp-meta-label">{m.label}</span>
                          <span className="vp-meta-value">{m.value}</span>
                        </span>
                      ))}
                    </div>
                    {changeReason && (
                      <div className="vp-change-reason">{changeReason}</div>
                    )}
                  </div>
                  <span className="vp-ago">{relativeAgo(s.capturedAt)}</span>
                </button>
                {action === 'active-pill' && (
                  <span className="vp-item-active-pill">Active</span>
                )}
                {action === 'set-active' && (
                  <button
                    type="button"
                    className="vp-item-action"
                    disabled={setActiveDisabled}
                    title={setActiveTooltip}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isSelectableLockVersion(run.setActiveRepresentative)) {
                        resolvedOnSetActiveFromLockVersion?.(run.setActiveRepresentative);
                      }
                    }}
                  >
                    Set Active
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
